import axios from 'axios';

const DOC_BASE = '/customer/document-api/v1';
const ALLOWED_MIME = ['.pdf', '.doc', '.docx'];
const MSG_DOC_ID_REQUIRED = 'Document id is required';

export const PA_GIS_PRODUCT_CODE = 3916;

export function isPaProduct(item) {
  if (!item) return false;
  const policyNo = String(item.policyNumber || '');
  if (/\/3916\//.test(policyNo) || /\/095\//.test(policyNo)) return true;
  const code = item.productCode;
  const name = String(item.productName || '').toUpperCase();
  return code === PA_GIS_PRODUCT_CODE
    || code === '3916'
    || code === 3916
    || name.includes('PERSONAL ACCIDENT')
    || name.includes('STUDENT');
}

export async function fetchPolicyDocuments(policyNo) {
  if (!policyNo) {
    return { success: false, documents: [], message: 'Policy number is required' };
  }
  try {
    const res = await axios.get(`${DOC_BASE}/getNBDocByPolicyNo`, {
      params: { policyNo: String(policyNo).trim() },
      validateStatus: () => true,
      _skipErrorRedirect: true,
    });
    if (res.status === 200 && Array.isArray(res.data)) {
      const documents = res.data.filter(
        (doc) => ALLOWED_MIME.includes(String(doc.mimeType || '').toLowerCase()),
      );
      return { success: true, documents };
    }
    return { success: false, documents: [], message: 'Unable to load policy documents' };
  } catch (e) {
    return {
      success: false,
      documents: [],
      message: e?.response?.data?.message || 'Failed to load policy documents',
    };
  }
}

/** Prefer UW policy wording — same file as Conversions "Policy Document". */
export function pickPolicyDocument(documents) {
  if (!documents?.length) return null;
  const match = documents.find((doc) => {
    const type = String(doc.docType || '').toLowerCase();
    const name = String(doc.actualName || '').toLowerCase();
    return type.includes('policy document')
      || name.includes('policy_document')
      || name.includes('policy document');
  });
  return match || documents[0];
}

export function buildDocumentViewUrl(docId) {
  const basePath = process.env.NODE_ENV === 'production' ? '/api' : '';
  return `${basePath}${DOC_BASE}/viewUWDocs/${docId}`;
}

function resolveDocumentFileName(doc, policyNumber, docId, fallbackExt = '.pdf') {
  const raw = typeof doc === 'object'
    ? (doc.actualName || `Policy_${policyNumber || docId}`)
    : `Policy_${policyNumber || docId}`;
  if (/\.[a-z0-9]+$/i.test(raw)) return raw;
  return `${raw}${fallbackExt}`;
}

function decodeBase64ToBlob(base64Data, mimeType = 'application/pdf') {
  const binary = atob(base64Data);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new Blob([bytes], { type: mimeType });
}

/** Fetch the actual file bytes from DocuWare (not the HTML viewer page). */
export async function fetchUwDocumentFile(docId) {
  if (!docId) {
    return { success: false, message: MSG_DOC_ID_REQUIRED };
  }
  try {
    const res = await axios.get(`${DOC_BASE}/getUWDocumentByDocId`, {
      params: { docId: String(docId) },
      validateStatus: () => true,
      _skipErrorRedirect: true,
    });
    const payload = res.data;
    if (res.status !== 200 || !payload || payload.empty === 'true' || !payload.byteData) {
      return { success: false, message: 'Document file is not available yet' };
    }
    const ext = String(payload.docMimetype || '.pdf').toLowerCase();
    const mime = ext.includes('pdf') ? 'application/pdf' : 'application/octet-stream';
    return {
      success: true,
      blob: decodeBase64ToBlob(payload.byteData, mime),
      extension: ext.startsWith('.') ? ext : `.${ext}`,
    };
  } catch (e) {
    return {
      success: false,
      message: e?.response?.data?.message || 'Failed to load document file',
    };
  }
}

function openBlobInNewTab(blob) {
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 120000);
}

function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.rel = 'noopener noreferrer';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Open in browser — uses real PDF bytes; falls back to DocuWare HTML viewer. */
export async function openPolicyDocument(doc) {
  const docId = typeof doc === 'object' ? doc.id : doc;
  if (!docId) return { success: false, message: MSG_DOC_ID_REQUIRED };

  const fileResult = await fetchUwDocumentFile(docId);
  if (fileResult.success) {
    openBlobInNewTab(fileResult.blob);
    return { success: true };
  }

  window.open(buildDocumentViewUrl(docId), '_blank', 'noopener,noreferrer');
  return { success: true, fallbackViewer: true };
}

/** Download a valid PDF/DOC file — not the HTML viewer shell from viewUWDocs. */
export async function downloadPolicyDocument(doc, policyNumber) {
  const docId = typeof doc === 'object' ? doc.id : doc;
  if (!docId) return { success: false, message: MSG_DOC_ID_REQUIRED };

  const fileResult = await fetchUwDocumentFile(docId);
  if (!fileResult.success) {
    return fileResult;
  }

  const fileName = resolveDocumentFileName(
    doc,
    policyNumber,
    docId,
    fileResult.extension || '.pdf',
  );
  triggerBlobDownload(fileResult.blob, fileName);
  return { success: true };
}

export function sleep(ms) {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

/**
 * Poll DocuWare until the policy document appears (UW upload is async after issuance).
 */
export async function pollPolicyDocument(policyNo, {
  intervalMs = 5000,
  maxAttempts = 24,
  onAttempt,
} = {}) {
  const pollAttempt = async (attempt) => {
    if (onAttempt) onAttempt(attempt, maxAttempts);
    const result = await fetchPolicyDocuments(policyNo);
    if (result.success) {
      const doc = pickPolicyDocument(result.documents);
      if (doc) {
        return { ready: true, document: doc, documents: result.documents };
      }
    }
    if (attempt >= maxAttempts) {
      return { ready: false, document: null, documents: [] };
    }
    await sleep(intervalMs);
    return pollAttempt(attempt + 1);
  };
  return pollAttempt(1);
}

export async function resolvePaPolicyDocument(policyNo) {
  const result = await fetchPolicyDocuments(policyNo);
  if (!result.success) return { ready: false, document: null, error: result.message };
  const doc = pickPolicyDocument(result.documents);
  return {
    ready: !!doc,
    document: doc,
    documents: result.documents,
    docViewUrl: doc ? buildDocumentViewUrl(doc.id) : null,
  };
}
